/**
 * LangPathEnforcer — single source of truth for the URL ↔ language pair.
 *
 *  Goal (per user direction): if a visitor is on the Japanese version of
 *  the site, every URL they navigate to should stay under `/ja/...`.
 *  Same for English (`/en/...`). No more silent flips between languages
 *  when the visitor clicks a link.
 *
 *  How it works:
 *    1. On every location change, look at the pathname.
 *    2. If the URL is in the exempt list (dashboard, admin, transactional
 *       email-token URLs), do nothing — those routes are not language-
 *       scoped.
 *    3. If the URL already has `/ja/` or `/en/` prefix, sync language
 *       state to that prefix (lets external/bookmarked links pick a lang).
 *    4. If the URL is a public route WITHOUT a lang prefix, redirect to
 *       `/{currentLang}{path}` so the URL reflects the active language.
 *
 *  Result: clicking any internal link — whether the link itself was
 *  written with or without a prefix — always lands the visitor on the
 *  current-language version of that page. No flipping.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';

// Routes that must NEVER be language-prefixed:
//  - /dashboard, /admin                    → behind login; lang lives in state
//  - /api                                  → backend proxy (shouldn't hit this
//                                            in the SPA but defensive guard)
//  - /verify-email, /reset-password,
//    /account/recovery, /federation,
//    /email-change                         → transactional, accessed via
//                                            email tokens; rewriting the URL
//                                            would break inbound links and
//                                            break token parsing on some
//                                            partner email clients
const EXEMPT_PREFIXES = [
  '/dashboard',
  '/admin',
  '/api',
  '/verify-email',
  '/reset-password',
  '/account/recovery',
  '/recovery',
  '/federation',
  '/email-change',
  // Token-bearing Early Access email links: their language is driven by the
  // registrant's stored preference (returned by the API), not the URL. Never
  // rewrite these — doing so showed English registrants a Japanese page and
  // risked dropping the ?token= query on canonicalisation.
  '/early-access/confirm',
  '/email/preferences',
  // Preview/test-only simulator harness — not a language-scoped public page.
  '/dev',
  // Private, non-indexed Swiss & High-Contrast design-system reference
  // build (2026-02). Self-contained EN/JA toggle inside the page itself —
  // must never be rewritten to /ja/design-preview or /en/design-preview.
  '/design-preview',
];

function isExempt(path) {
  return EXEMPT_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

export default function LangPathEnforcer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();

  useEffect(() => {
    const path = location.pathname;

    // Root path — canonicalise to `/{lang}` so the URL always reflects
    // the active language (per customer rule: visiting japantollreceipts.jp
    // must default to /ja unless the visitor explicitly chose English).
    // `replace: true` avoids polluting browser history with the bare `/`
    // entry, so the back button still works as expected.
    if (path === '/') {
      const target = `/${lang}${location.search}${location.hash}`;
      navigate(target, { replace: true });
      return;
    }

    if (isExempt(path)) return;

    // If URL has a lang prefix, sync state to match.
    if (path === '/ja' || path.startsWith('/ja/')) {
      if (lang !== 'ja') setLang('ja');
      return;
    }
    if (path === '/en' || path.startsWith('/en/')) {
      if (lang !== 'en') setLang('en');
      return;
    }

    // Public URL without a lang prefix → canonicalise to current lang.
    const target = `/${lang}${path}${location.search}${location.hash}`;
    navigate(target, { replace: true });
  }, [location.pathname, location.search, location.hash, lang, navigate, setLang]);

  return null;
}
